"use client";

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Share2, Star, StarHalf, MapPin } from "lucide-react";
import { useBookingModal } from "@/contexts/BookingModalContext";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const WHATSAPP_URL = "https://wa.me/5492944636295";

function StarRow({ score }: { score: number }) {
  const nodes: ReactNode[] = [];
  for (let i = 0; i < 5; i++) {
    if (score >= i + 1) {
      nodes.push(
        <Star
          key={i}
          className="h-3.5 w-3.5 fill-amber-400 text-amber-400 sm:h-4 sm:w-4"
          aria-hidden
        />,
      );
    } else if (score >= i + 0.5) {
      nodes.push(
        <StarHalf
          key={i}
          className="h-3.5 w-3.5 fill-amber-400 text-amber-400 sm:h-4 sm:w-4"
          aria-hidden
        />,
      );
    } else {
      nodes.push(
        <Star
          key={i}
          className="h-3.5 w-3.5 fill-slate-200 text-slate-200 sm:h-4 sm:w-4"
          aria-hidden
        />,
      );
    }
  }
  return <div className="flex items-center gap-0.5">{nodes}</div>;
}

export type PitchPageHeroProps = {
  eyebrowKey: string;
  titleKey: string;
  descriptionKey: string;
  shareTitleKey: string;
  /** e.g. 5 or 4.8 — drives star display */
  ratingScore: number;
  /** Passed to `canchaPitchPage.ratingsCount` */
  reviewCount: number;
};

export default function PitchPageHero({
  eyebrowKey,
  titleKey,
  descriptionKey,
  shareTitleKey,
  ratingScore,
  reviewCount,
}: PitchPageHeroProps) {
  const { t } = useTranslation();
  const { openModal } = useBookingModal();

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: t(shareTitleKey),
          url: globalThis.location.href,
        });
      } else {
        await navigator.clipboard.writeText(globalThis.location.href);
        alert(t("canchaPitchPage.linkCopied"));
      }
    } catch {
      // share cancelled or clipboard failed
    }
  };

  const displayScore = Number.isInteger(ratingScore)
    ? ratingScore.toFixed(1)
    : ratingScore.toFixed(1);

  return (
    <div className="relative border-b border-slate-200/80 bg-gradient-to-b from-slate-50 via-white to-white">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        aria-hidden
        style={{
          backgroundImage:
            "radial-gradient(ellipse 80% 50% at 50% -20%, rgba(34, 72, 214, 0.12), transparent 55%)",
        }}
      />
      <div className="relative mx-auto w-full max-w-6xl px-4 pt-10 pb-10 sm:px-6 sm:pt-12 sm:pb-12">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:justify-between lg:gap-12">
          <div className="min-w-0 flex-1 space-y-5">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              {t(eyebrowKey)}
            </p>
            <h1 className="text-balance text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-[2.75rem] lg:leading-[1.12]">
              {t(titleKey)}
            </h1>
            <p className="max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              {t(descriptionKey)}
            </p>
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 text-sm text-slate-600 shadow-sm backdrop-blur-sm">
                <MapPin className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                {t("canchaPitchPage.cityRegion")}
              </span>
              <div
                className="inline-flex items-center gap-2 rounded-full border border-amber-200/80 bg-gradient-to-r from-amber-50 to-amber-50/30 px-3 py-1.5 shadow-sm"
                aria-label={t("canchaPitchPage.ratingsCount", { count: reviewCount })}
              >
                <StarRow score={ratingScore} />
                <span className="text-sm font-semibold tabular-nums text-slate-800">
                  {displayScore}
                </span>
                <span className="hidden h-3 w-px bg-amber-200/90 sm:block" aria-hidden />
                <span className="text-xs font-medium text-slate-600 sm:text-sm">
                  {t("canchaPitchPage.ratingsCount", { count: reviewCount })}
                </span>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 flex-col justify-center gap-3 lg:w-[min(100%,20.5rem)]">
            <button
              type="button"
              onClick={openModal}
              className="w-full rounded-xl bg-primary px-5 py-3.5 text-sm font-semibold text-white shadow-md shadow-primary/25 transition hover:bg-primary-strong hover:shadow-lg active:scale-[0.98]"
            >
              {t("header.bookSlot")}
            </button>
            <p className="text-xs leading-snug text-slate-500 lg:text-left">{t("faq.a6")}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleShare}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50/80 py-2.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-white"
              >
                <Share2 className="h-4 w-4 shrink-0" aria-hidden />
                {t("canchaPitchPage.share")}
              </button>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-emerald-200/80 bg-emerald-50/60 py-2.5 text-xs font-semibold text-emerald-800 transition hover:border-emerald-300 hover:bg-emerald-50"
              >
                <WhatsAppIcon size={16} className="h-4 w-4 shrink-0" />
                {t("canchaPitchPage.inquiry")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
